import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { Flag, ExternalLink } from "lucide-react";

export default function AboutPage() {
    return (
        <div className="min-h-screen bg-[#0B1220]">
            <Header />

            {/* Hero with stacked logo on contrasting card */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-4">About</div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-8">
                    A structured library for<br />complicated questions.
                </h1>
                <p className="text-lg text-[#94A3B8] leading-relaxed max-w-3xl mb-12">
                    The Context Project is a structured research library that explains complex,
                    controversial, and misunderstood topics using evidence-based breakdowns.
                </p>

                <div className="bg-white rounded-2xl p-10 sm:p-14 flex items-center justify-center border border-[#243044]" data-testid="about-logo-card">
                    <img
                        src="/brand/logo-horizontal.png"
                        alt="The Context Project"
                        className="max-w-md w-full h-auto"
                    />
                </div>
            </section>

            {/* Principles */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="space-y-8 text-[15px] text-[#CBD5E1] leading-[1.85]">
                    <p>
                        We prioritise <span className="text-white font-semibold">clarity, structure, evidence and neutrality</span>.
                        Every topic follows the same rigorous template — official position, system breakdown, critical questions,
                        tiered evidence (documented, reported, debated), supporting and critical views, real-world examples,
                        what's unclear, and where it could go.
                    </p>
                    <p>
                        We never present claims without evidence. We separate fact from interpretation. We label
                        independently funded research, industry-funded research, and government sources clearly. We do not
                        automatically dismiss any source — we let you interpret the evidence.
                    </p>
                    <p>
                        Every topic shows two visible scores: <span className="text-[#56CCF2] font-semibold">Confidence</span> (how
                        well-supported the documented evidence is) and <span className="text-[#E2B95C] font-semibold">Concern</span> (how
                        much real-world impact and unresolved risk the topic carries).
                    </p>
                </div>

                <div className="mt-12">
                    <Link
                        to="/methodology"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2F80ED] hover:bg-[#2F80ED]/90 text-white text-sm font-medium transition-colors"
                        data-testid="about-methodology-cta"
                    >
                        Read the full methodology
                        <ExternalLink className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            {/* Author */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-[#243044]">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-8">Compiled by</div>
                <div className="flex flex-col sm:flex-row items-start gap-8">
                    <div className="w-48 h-48 rounded-2xl overflow-hidden border border-[#243044] shrink-0 bg-[#111827]" data-testid="about-author-photo">
                        <img
                            src="/brand/bill-mathers.png"
                            alt="Bill Mathers — The Context Project"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex-1 pt-2">
                        <h2 className="text-3xl font-bold text-white mb-2">
                            <a
                                href="https://www.facebook.com/people/Bill-Mathers/61578352413913/"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="about-bill-mathers-fb"
                                className="hover:text-[#56CCF2] transition-colors"
                            >
                                Bill Mathers
                            </a>
                        </h2>
                        <p className="text-sm text-[#56CCF2] uppercase tracking-wider mb-5">Founder · The Context Project</p>
                        <p className="text-[15px] text-[#CBD5E1] leading-relaxed max-w-xl">
                            Every topic in the library is structured, sourced, and compiled to the same standard. Errors get reported,
                            reviewed, and fixed — the library is treated as a working document, not a finished verdict.
                        </p>
                        <div className="mt-6 flex flex-wrap items-center gap-4">
                            <a
                                href="https://thecontextproject.com"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-[#56CCF2] hover:text-white transition-colors"
                            >
                                Visit thecontextproject.com
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                            <a
                                href="https://www.facebook.com/TheContextProject/"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="about-tcp-fb"
                                className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#56CCF2] transition-colors"
                            >
                                Facebook
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                            <a
                                href="https://www.facebook.com/people/Bill-Mathers/61578352413913/"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="about-bill-mathers-fb-row"
                                className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#56CCF2] transition-colors"
                            >
                                Bill Mathers on Facebook
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                            <a
                                href="https://thecontextproject.com/pages/report-an-issue"
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#56CCF2] transition-colors"
                            >
                                <Flag className="w-3.5 h-3.5" />
                                Report an issue
                            </a>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
